import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { I18nProvider } from "@/i18n/I18nProvider";
import { About } from "@/pages/About";
import { Buy } from "@/pages/Buy";
import { Contact } from "@/pages/Contact";
import { Home } from "@/pages/Home";
import { Media } from "@/pages/Media";
import { NotFound } from "@/pages/NotFound";
import { World } from "@/pages/World";
import { RouterContext, normalizePath } from "@/router";

const routes = {
  "/": Home,
  "/about": About,
  "/world": World,
  "/media": Media,
  "/buy": Buy,
  "/contact": Contact
} as const;

export function App() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));

  const navigate = useCallback((href: string) => {
    const nextPath = normalizePath(href);
    if (nextPath === normalizePath(window.location.pathname)) {
      return;
    }
    window.history.pushState({}, "", nextPath);
    setPath(nextPath);
  }, []);

  useEffect(() => {
    const handlePopState = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [path]);

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("reveal-waiting");
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
        element.classList.add("is-visible");
        return;
      }
      element.classList.add("reveal-waiting");
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, [path]);

  const routerValue = useMemo(() => ({ path, navigate }), [path, navigate]);
  const Page = routes[path as keyof typeof routes] ?? NotFound;

  return (
    <RouterContext.Provider value={routerValue}>
      <I18nProvider>
        <Layout>
          <Page />
        </Layout>
      </I18nProvider>
    </RouterContext.Provider>
  );
}
